import { useEffect } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"
import { usePageTracking } from "../../../../../hooks"
import { AnalyticsPageCategory, AnalyticsPageType } from "../../../../../models"

import {
  createMetadata,
  fetchAllLicences,
  fetchChapterListByLicenceIds,
  fetchDetailedAssetList,
  fetchMetadata,
  fetchRegionListByChapterAndLicenceIds,
  fetchThemeListByParentAndLicenceIds,
  loadDefaultMetadata,
  updateMetadata
} from "../../../../../redux"

import type {
  AppState,
  AppThunkDispatch,
  Asset,
  Chapter,
  Licence,
  Metadata,
  MetadataActions,
  Region,
  Theme
} from "../../../../../redux"
import { BackButton, PageContainer } from "../../../../molecules"
import { MapMetadataManagerForm } from "../../../../organisms"

export interface IMapMetadataManagerPageProps {
  metadata: Metadata
  chapters: Chapter[]
  regions: Region[]
  regionThemes: Theme[]
  chapterThemes: Theme[]
  licences?: Licence[]
  assets: Asset[]
  fetchAssets: () => void
  fetchChapters: (licenceIds: string[]) => void
  fetchRegions: (chapterId: string, licenceIds: string[]) => void
  fetchThemes: (chapterId: string | undefined, regionId: string | undefined, licenceIds: string[]) => void
  fetchLicences: () => void
  fetchMetadata: (id: string) => void
  fetchDefaultMetadata: () => void
  createMetadata: (metadata: Metadata) => void
  updateMetadata: (metadata: Metadata) => void
}

export interface IMapMetadataManagerRouteParams {
  metadataId?: string
}

function MapMetadataManagerPageView(
  props: IMapMetadataManagerPageProps & IMapMetadataManagerRouteParams
): ReactElement {
  const {
    metadataId,
    metadata,
    chapters,
    regions,
    regionThemes,
    chapterThemes,
    licences,
    assets,
    fetchAssets,
    fetchChapters,
    fetchRegions,
    fetchThemes,
    fetchLicences,
    fetchMetadata,
    fetchDefaultMetadata,
    createMetadata,
    updateMetadata
  } = props

  usePageTracking(AnalyticsPageCategory.ContentManager, AnalyticsPageType.Map, metadataId)
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchAssets()
    fetchLicences()
    if (metadataId && metadataId !== metadata.id) {
      fetchMetadata(metadataId)
    } else if (!metadataId) {
      fetchDefaultMetadata()
    }
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (metadata.id && metadata.licenceIds && metadata.licenceIds.length > 0) {
      if (chapters.length === 0) fetchChapters(metadata.licenceIds)
      if (metadata.chapterId) {
        fetchRegions(metadata.chapterId, metadata.licenceIds)
        fetchThemes(metadata.chapterId, undefined, metadata.licenceIds)
      }
    }
  }, [metadata])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (
      metadata.id &&
      metadata.licenceIds &&
      metadata.licenceIds.length > 0 &&
      metadata.regionId &&
      metadata.regionId !== " "
    ) {
      fetchThemes(undefined, metadata.regionId, metadata.licenceIds)
    }
  }, [regions])

  function hasLoaded(): boolean {
    if ((metadataId === metadata.id && assets) || (!metadataId && assets)) {
      return true
    }
    return false
  }

  function handleLicencesChanged(licenceIds: string[]) {
    fetchChapters(licenceIds)
  }

  function handleChapterChange(chapterId: string, licenceIds: string[]) {
    fetchRegions(chapterId, licenceIds)
    fetchThemes(chapterId, undefined, licenceIds)
  }

  function handleRegionChange(regionId: string, licenceIds: string[]) {
    fetchThemes(undefined, regionId, licenceIds)
  }

  function handleMetadataSave(metadata: Metadata): void {
    if (metadata.id) {
      updateMetadata(metadata)
    } else {
      createMetadata(metadata)
    }
  }

  return (
    <PageContainer smallPadding hasLoaded={hasLoaded()} id="map-manager-page">
      <BackButton />
      {licences && assets && (
        <MapMetadataManagerForm
          metadata={metadata}
          chapters={chapters}
          regions={regions}
          regionThemes={regionThemes}
          chapterThemes={chapterThemes}
          licences={licences}
          assets={assets}
          onChapterChange={handleChapterChange}
          onRegionChange={handleRegionChange}
          saveMetadata={handleMetadataSave}
          onLicenceIdsChanged={handleLicencesChanged}
        />
      )}
    </PageContainer>
  )
}

function mapStateToProps(state: AppState, ownProps: IMapMetadataManagerRouteParams) {
  return {
    metadataId: ownProps.metadataId,
    metadata: state.metadata.metadata,
    chapters: state.chapters.chapters ? state.chapters.chapters : [],
    regions: state.region.regions,
    regionThemes: state.theme.regionThemes,
    chapterThemes: state.theme.chapterThemes,
    licences: state.licences.licences,
    assets: state.assets.assets
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MetadataActions>) {
  return {
    fetchAssets: () => dispatch(fetchDetailedAssetList()),
    fetchChapters: (licenceIds: string[]) => dispatch(fetchChapterListByLicenceIds(licenceIds)),
    fetchRegions: (chapterId: string, licenceIds: string[]) =>
      dispatch(fetchRegionListByChapterAndLicenceIds(chapterId, licenceIds)),
    fetchThemes: (chapterId: string | undefined, regionId: string | undefined, licenceIds: string[]) =>
      dispatch(fetchThemeListByParentAndLicenceIds(chapterId, regionId, licenceIds)),
    fetchLicences: () => dispatch(fetchAllLicences()),
    fetchMetadata: (id: string) => dispatch(fetchMetadata(id)),
    fetchDefaultMetadata: () => dispatch(loadDefaultMetadata()),
    createMetadata: (metadata: Metadata) => dispatch(createMetadata(metadata)),
    updateMetadata: (metadata: Metadata) => dispatch(updateMetadata(metadata))
  }
}

export const MapMetadataManagerPage = connect(mapStateToProps, mapDispatchToProps)(MapMetadataManagerPageView)
